import { observable, computed, action, runInAction } from "mobx";
import merge from "lodash/merge";
import { Subscription } from "rxjs";

import {
	Event,
	EventType,
	LiveStatus,
	PageableOfferResponse,
} from "@gp/models";
import { PrematchOfferStore } from "@gp/offer";

import { ConsoleLogger, LogLevel } from "@gp/utility";
import {
	TOfferRequest,
	ISubscriptionRequest,
	EventFilterParams,
} from "@gp/hub";

import { parsePageNumberToValid } from "@utils";
import betRules from "@betting-rules";
import PrematchOfferApiService from "@api-services/offer/PrematchOfferApiService";

import { BettingTypeSelectorsStore } from "../shared/BettingTypeSelectorsStore";
import { LoaderStore } from "@state/stores/common";
import type { RootStore } from "@state/stores/types";
import { TSearchPageParams } from "@features/offer/mobile/search/SearchPage";
import { SearchAction } from "@state/stores/offer/search/SearchStore";
import { updateUrlParams } from "@state/utils";
import { parseSearchPageParameters } from "@utils/parsers/parseSearchPageParameters";

const DefaultConfig = {
	columns: [],
};

export default class SearchViewStore extends PrematchOfferStore {
	config = DefaultConfig;
	loader: LoaderStore;
	rootStore: RootStore;

	//#region observables

	@observable public isFetchingData = false;
	@observable isInitialize = true;
	private subscription: Subscription | null;
	params: TSearchPageParams | null = null;

	//#endregion observables

	//#region computed

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed get isEmpty() {
		/** Override is empty */
		return this.eventsMap.size === 0 && !this.isInitialize;
	}

	@computed get searchElementsCount() {
		return this.eventsMap.size;
	}

	//#endregion computed

	//#region  constructor

	constructor(
		rootStore: RootStore,
		config = undefined,
		bettingTypeSelectorsStore?: BettingTypeSelectorsStore
	) {
		super({
			pageSize: 1000,
			logger: new ConsoleLogger(false, LogLevel.ERROR),
			removeDelay: 10,
			throttle: 4,
			enableThrottling: true,
			bettingTypeSelectorsStore:
				bettingTypeSelectorsStore || new BettingTypeSelectorsStore(),
			customConfiguration: betRules,
		});

		if (config != null) {
			this.config = merge({}, DefaultConfig, config);
		}

		this.rootStore = rootStore;
		this.loader = new LoaderStore();

		this.pageNumber = 1;
		this.pageSize = 1000;
	}

	//#endregion constructor

	//#region data fetching

	@action.bound
	onInitialize(pageNumber: number, params: TSearchPageParams) {
		if (params.action == null) {
			console.error("Invalid action", params.action);
			return;
		}
		const parsedParameters = parseSearchPageParameters(params);

		if (Number.isNaN(parsedParameters.action)) {
			console.error("Invalid action", parsedParameters.action);
			return;
		}
		this.params = parsedParameters;

		this.fetchData(pageNumber);
	}

	@action.bound
	async fetchData(pageNumber: number) {
		this.loader.suspend();
		this.isFetchingData = true;

		this.pageNumber = parsePageNumberToValid(pageNumber);
		updateUrlParams({ page: this.pageNumber }, true);

		this.disposeSubscription();

		let offerResponse: PageableOfferResponse | null = null;
		try {
			offerResponse = await PrematchOfferApiService.getOfferData(
				this.getOfferRequest()
			);
		} catch (err) {
			console.error(err);
			this.onDoneFetching();
			return;
		}

		runInAction(() => {
			if (offerResponse != null) {
				this.handleOfferAssign(offerResponse);
			}

			if (this.lookupsStore.tournaments.size > 0) {
				// If we are creating live subscription then live subscription will call this.onDoneFetching
				this.createLiveSubscription();
			} else {
				this.onDoneFetching();
			}
		});
	}

	@action.bound
	handleOfferAssign(offer: PageableOfferResponse) {
		this.assignOfferData(offer);
	}

	@action.bound
	createLiveSubscription() {
		const subscription = this.getLiveSubscriptionRequest();
		if (!subscription) {
			console.error("Invalid params,", this.params);
			this.onDoneFetching();
			return;
		}
		// add live subscription
		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscription)
			.subscribe(
				(response) => {
					runInAction(() => {
						const events = response?.offerChanges?.updates
							?.map((offer) => offer?.event)
							?.filter((event): event is Event => event != null);
						this.resetEvents(events);
						this.assignLiveData(response, true);
						this.onDoneFetching();
					});
				},
				(err) => {
					console.error(err);
					this.onDoneFetching();
				}
			);
	}

	@action.bound
	onDoneFetching() {
		this.loader.resume();
		this.isInitialize = false;
		this.isFetchingData = false;
	}

	@action.bound
	resetEvents(eventIds?: Event[]) {
		eventIds?.forEach((event) => {
			const oldEvent = this.eventsMap.get(event.id);
			if (!oldEvent) {
				return;
			}
			const isLive = event.liveStatus === LiveStatus.LIVE;
			if (oldEvent.isLive == isLive) {
				return;
			}
			this.deleteEvent(oldEvent.id);
			const sportOffer = this.eventsInSports.sports.find(
				(sport) => sport.id == event.sportId
			);
			if (sportOffer) {
				sportOffer.removeEvent(event.id);
				sportOffer.updateEventCount(sportOffer.eventCount - 1);
			}
		});
	}
	//#endregion data fetching

	//#region filter
	getOfferRequest(): TOfferRequest {
		const baseOfferRequest: TOfferRequest = {
			paging: {
				pageNumber: this.pageNumber,
				pageSize: this.pageSize,
			},
			filter: null,
			grouping: "tournamentGroup",
		};

		switch (this.params?.action) {
			case SearchAction.SEARCH:
			case SearchAction.PICK_FIRST: {
				baseOfferRequest.filter = this.getSearchOfferFilter();
				break;
			}
			case SearchAction.PICK: {
				baseOfferRequest.filter = this.getPredefined();
				break;
			}
		}
		return baseOfferRequest;
	}

	getPredefined(): TOfferRequest["filter"] | null {
		if (!this.params) {
			return null;
		}

		const tournamentFilter: EventFilterParams = {};
		if (this.params.sportId)
			tournamentFilter["sportId"] = this.params.sportId;
		if (this.params.categoryId)
			tournamentFilter["sportCategoryId"] = this.params.categoryId;
		if (this.params.tournamentId)
			tournamentFilter["tournamentId"] = this.params.tournamentId;

		let filters: TOfferRequest["filter"] = [
			{
				...tournamentFilter,
				...this.getPrematchBettingTypeFilter(),
			},
			{
				...tournamentFilter,
				...this.getLiveBettingTypeFilter(),
			},

			{
				...tournamentFilter,
				liveStatus: LiveStatus.PREMATCH,
				eventType: {
					eq: [EventType.SHORT_TERM, EventType.LONG_TERM],
				},
			},
		];

		return filters;
	}

	/** Pageable request. */
	getSearchOfferFilter(): TOfferRequest["filter"] {
		const searchFilter =
			this.rootStore.searchBarViewStore.searchFilterArray;

		let filters: TOfferRequest["filter"] = [
			...this.getFilterForTeam(0), // Team one
			...this.getFilterForTeam(1), // Team one
			...this.getFilterForTeam(2), // Team two
			...this.getFilterForTeam(3), // Team one
			...this.getFilterForTournamentName(4),
			...this.getFilterForTournamentName(5),
			...this.getFilterForCategoryName(6),
			...this.getFilterForCategoryName(7),

			{
				...searchFilter[8], // Event name
				liveStatus: LiveStatus.PREMATCH,
				eventType: {
					eq: [EventType.SHORT_TERM, EventType.LONG_TERM],
				},
			},
		];

		return filters;
	}

	getFilterForTeam(teamIdx: number) {
		const searchFilter =
			this.rootStore.searchBarViewStore.searchFilterArray;

		return [
			/** Normal offer filter for one team. */
			{
				...searchFilter[teamIdx], // Team name filter
				...this.getPrematchBettingTypeFilter(),
			},
			/** Live offer filter for one team. */
			{
				...searchFilter[teamIdx], // Team name filter
				...this.getLiveBettingTypeFilter(),
			},
		];
	}
	getFilterForCategoryName(index: number) {
		const searchFilter =
			this.rootStore.searchBarViewStore.searchFilterArray;

		return [
			{
				...searchFilter[index],
				...this.getPrematchBettingTypeFilter(),
			},
			{
				...searchFilter[index],
				...this.getLiveBettingTypeFilter(),
			},
		];
	}
	getFilterForTournamentName(index: number) {
		const searchFilter =
			this.rootStore.searchBarViewStore.searchFilterArray;

		return [
			/** Normal offer filter for one team. */
			{
				...searchFilter[index], // Team name filter
				...this.getPrematchBettingTypeFilter(),
			},
			/** Live offer filter for one team. */
			{
				...searchFilter[index], // Team name filter
				...this.getLiveBettingTypeFilter(),
			},
		];
	}

	getPrematchBettingTypeFilter() {
		const prematchBettingTypes = this.OfferMapper.getBettingTypes(
			"prematch",
			betRules
		);
		return {
			liveStatus: LiveStatus.PREMATCH,
			eventType: EventType.NORMAL,
			offers: [
				{
					bettingType: {
						abrv: {
							eq: prematchBettingTypes.normal,
						},
					},
				},
				{
					bettingType: {
						abrv: {
							eq: prematchBettingTypes.marginal,
						},
					},
					isFavorite: true,
				},
			],
		};
	}

	getLiveBettingTypeFilter() {
		const liveBettingTypes = this.OfferMapper.getBettingTypes(
			"live",
			betRules
		);

		return {
			liveStatus: LiveStatus.LIVE,
			eventType: EventType.NORMAL,
			offers: [
				{
					bettingType: {
						abrv: {
							eq: liveBettingTypes.normal,
						},
					},
				},
				{
					bettingType: {
						abrv: {
							eq: liveBettingTypes.marginal,
						},
					},
					isFavorite: true,
				},
			],
		};
	}

	getLiveSearchFilter() {
		if (!this.params) {
			return null;
		}

		switch (this.params?.action) {
			case SearchAction.SEARCH:
			case SearchAction.PICK_FIRST: {
				return this.rootStore.searchBarViewStore.searchFilterArray;
			}
			case SearchAction.PICK: {
				const tournamentFilter: EventFilterParams = {};
				if (this.params.sportId)
					tournamentFilter["sportId"] = this.params.sportId;
				if (this.params.categoryId)
					tournamentFilter["sportCategoryId"] =
						this.params.categoryId;
				if (this.params.tournamentId)
					tournamentFilter["tournamentId"] = this.params.tournamentId;

				return [tournamentFilter];
			}
		}
		return this.rootStore.searchBarViewStore.searchFilterArray;
	}

	/** Live request. */
	getLiveSubscriptionRequest(): ISubscriptionRequest | null {
		const liveBettingTypes = this.OfferMapper.getBettingTypes(
			"live",
			betRules
		);
		const searchFilter = this.getLiveSearchFilter();
		if (!searchFilter) {
			return null;
		}

		const appendLiveStatusToFilter = searchFilter.map<EventFilterParams>(
			(filter: EventFilterParams) => ({
				...filter,
				liveStatus: LiveStatus.LIVE,
			})
		);
		return {
			subscriptionId:
				"search-" + this.rootStore.searchBarViewStore.searchTerm,
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: appendLiveStatusToFilter,
				},
				{
					name: "betOffer",
					filter: [
						{
							bettingType: {
								abrv: {
									eq: liveBettingTypes.normal,
								},
							},
						},
						{
							bettingType: {
								abrv: {
									eq: liveBettingTypes.marginal,
								},
							},
							isFavorite: true,
						},
					],
				},
			],
		};
	}

	//#endregion filter

	//#region disposers

	@action.bound
	onDispose() {
		this.disposeSubscription();
		this.onReset();
	}

	@action.bound
	disposeSubscription() {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	onReset() {
		this.loader.suspend();
		this.isInitialize = true;

		this.reset();
	}

	//#endregion disposers
}
